import { useMemo, useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { boardIndex } from '@/mocks/home';

const toneStyles: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-700',
  secondary: 'bg-secondary-100 text-secondary-800',
};

const filters = [
  { id: 'all', label: '全部板块' },
  { id: 'hot', label: '高热板块' },
  { id: 'new', label: '本周新增' },
];

export default function BoardIndex() {
  const [active, setActive] = useState('all');

  const list = useMemo(() => {
    if (active === 'hot') return boardIndex.filter((item) => item.tone === 'primary');
    if (active === 'new') return boardIndex.filter((item) => item.tone === 'accent');
    return boardIndex;
  }, [active]);

  return (
    <section id="board-index" className="w-full bg-background-100 border-y border-background-200 py-14 md:py-20 px-4 md:px-8">
      <div className="w-full max-w-[1180px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-7 md:mb-9">
          <div className="lg:max-w-2xl">
            <SectionHeading
              eyebrow="Board Index"
              icon="ri-layout-grid-line"
              title="板块索引：全站内容分区一页总览"
              desc="按题材拆分的 12 个内容板块，每个板块独立归档、独立更新。先在这里定位你关注的分区，再进去按时间线回看完整脉络。"
            />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-1 p-1 rounded-full border border-background-300 bg-background-50">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setActive(filter.id)}
                  className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    active === filter.id
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {list.map((board, index) => (
            <a
              key={board.id}
              href="#flash"
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 transition-all duration-300 hover:border-primary-300 hover:-translate-y-0.5 cursor-pointer"
            >
              <div className="flex items-start justify-between gap-3">
                <span className={`w-11 h-11 flex items-center justify-center rounded-lg ${toneStyles[board.tone]}`}>
                  <i className={`${board.icon} text-xl`}></i>
                </span>
                <span className="font-heading text-sm text-background-400">
                  {String(index + 1).padStart(2, '0')}
                </span>
              </div>

              <h3 className="mt-4 font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-700 transition-colors">
                {board.name}
              </h3>
              <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3">
                {board.desc}
              </p>

              <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  收录 <span className="text-primary-700 font-medium">{board.count}</span> 条
                </span>
                <span className="flex items-center gap-1.5 flex-wrap justify-end">
                  {board.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-md bg-background-100 text-[11px] text-foreground-600 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}